import { computed, onUnmounted, type Component, type ComputedRef } from 'vue';
import { useRoute } from 'vue-router';

// COMPONENTS
import SuccessTransaction from '@/modules/transaction/views/Success/index.vue';
import SuccessRecharge from '@/modules/cashIn/views/SuccessRecharge/SuccessRecharge.vue';
import kushkiTransactionStatus from '@/modules/cashIn/views/kushkiTransactionStatus/index.vue';
import WompiStatus from '@/modules/cashIn/views/WompiStatus/index.vue';
import InswitchStatus from '@/modules/cashIn/views/InswitchStatus/index.vue';

// CONSTANTS
import {
  INSWITCH_SESSION_KEYS,
  INSWITCH_CALLBACK_QUERY_SUCCESS,
  INSWITCH_CALLBACK_QUERY_ERROR,
} from '@/shared/inswitch/constants/inswitch.constant';
import { WOMPI_SESSION_KEYS } from '@/modules/cashIn/constants/wompi.constants';
import { PAYMENT_CALLBACK_DISCRIMINATOR } from '@/shared/common/constants/paymentCallback.constant';

// UTILS
import { resolvePaymentCallbackType } from '@/shared/common/utils/paymentCallbackType.util';
import { clearInswitchSession } from '@/shared/inswitch/utils/inswitchSession.util';
import { firstRouteValue } from '@/shared/common/utils/routeValue.util';

const successComponents: Record<string, Component> = {
  transaction: SuccessTransaction,
  cashin: SuccessRecharge,
  [PAYMENT_CALLBACK_DISCRIMINATOR.VOUCHER]: kushkiTransactionStatus,
  [PAYMENT_CALLBACK_DISCRIMINATOR.WOMPI_SUCCESS]: WompiStatus,
  'wompi-error': WompiStatus,
  'wompi-warning': WompiStatus,
  [INSWITCH_CALLBACK_QUERY_SUCCESS]: InswitchStatus,
  [INSWITCH_CALLBACK_QUERY_ERROR]: InswitchStatus,
};

/**
 * Resuelve que pantalla de resultado renderizar tras el retorno de una pasarela de pago,
 * y libera el contexto InSwitch de `sessionStorage` al salir de la vista.
 * @returns El componente de resultado correspondiente al discriminador de la ruta, o `null` si ninguno aplica.
 */
export function useSuccessTransactionView(): {
  nameSuccessView: ComputedRef<Component | null>;
} {
  const route = useRoute();

  onUnmounted(clearInswitchSession);

  const isWompiTransaction = computed(() => {
    const hasWompiReferenceCode = sessionStorage.getItem(WOMPI_SESSION_KEYS.REFERENCE_CODE);
    const hasWompiPaymentId = sessionStorage.getItem(WOMPI_SESSION_KEYS.PAYMENT_ID);
    return Boolean(hasWompiReferenceCode || hasWompiPaymentId);
  });

  const inswitchQueryRef = computed(() => {
    const transactionReference = firstRouteValue(route.query?.transactionReference);
    const referenceCode = firstRouteValue(route.query?.referenceCode);
    return transactionReference || referenceCode;
  });

  const isInswitchTransactionContext = computed(() => {
    if (inswitchQueryRef.value !== '') return true;
    const stored = sessionStorage.getItem(INSWITCH_SESSION_KEYS.TRANSACTION_REFERENCE);
    return Boolean(stored && stored.trim() !== '');
  });

  const type = computed(() =>
    resolvePaymentCallbackType({
      query: route.query,
      hasWompiReference: isWompiTransaction.value,
      hasInswitchReference: isInswitchTransactionContext.value,
    })
  );

  const nameSuccessView = computed(() => {
    const componentKey = String(type.value);
    return successComponents[componentKey] || null;
  });

  return { nameSuccessView };
}
